/**
 * Shopify Variant Visualizer Component
 * 4-column layout: Formato | Prezzo/u (s.IVA) | s.IVA | c.IVA
 * + Reseller pricing with strikethrough
 * + Shopify translations
 */

const { useState, useEffect } = React;

const VariantVisualizer = ({ productData }) => {
  const [selectedVariant, setSelectedVariant] = useState(
    productData?.variants?.[0]?.id || null
  );
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const reseller = window.variantVisualizerReseller || { isReseller: false, discount: 0 };

  const applyResellerDiscount = (priceStr) => {
    if (!reseller.isReseller || !reseller.discount || !priceStr || priceStr === '-') return priceStr;
    const numeric = parseFloat(priceStr.replace('€', '').replace(/\s/g, '').replace('.', '').replace(',', '.'));
    if (isNaN(numeric)) return priceStr;
    const discounted = numeric * (1 - reseller.discount);
    return '€ ' + discounted.toFixed(2).replace('.', ',');
  };

  const calcExcVAT = (incVATStr) => {
    if (!incVATStr || incVATStr === '-') return '-';
    const numeric = parseFloat(incVATStr.replace('€', '').replace(/\s/g, '').replace('.', '').replace(',', '.'));
    if (isNaN(numeric)) return '-';
    return '€ ' + (numeric / 1.22).toFixed(2).replace('.', ',');
  };

  const calcUnitExcVAT = (unitStr) => {
    if (!unitStr || unitStr === '-') return '-';
    const match = unitStr.match(/€\s*([\d.,]+)(\/.*)/);
    if (!match) return '-';
    const numeric = parseFloat(match[1].replace('.', '').replace(',', '.'));
    if (isNaN(numeric)) return '-';
    return '€ ' + (numeric / 1.22).toFixed(2).replace('.', ',') + match[2];
  };

  useEffect(() => {
    const beYoursQuantityInput = document.querySelector('[name="quantity"]');
    if (beYoursQuantityInput) {
      beYoursQuantityInput.value = quantity;
      beYoursQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, [quantity]);

  useEffect(() => {
    const fixProgressBarWidth = (bar) => {
      const inventoryQty = parseInt(bar.getAttribute('data-quantity') || bar.getAttribute('data-inventory-quantity') || 0);
      const max = parseInt(bar.getAttribute('data-max') || bar.getAttribute('max') || 100);
      const correctPercentage = Math.min((inventoryQty / max) * 100, 100);
      const currentWidth = bar.style.getPropertyValue('--progress-bar-width');
      if (currentWidth) {
        const currentValue = parseFloat(currentWidth);
        if (currentValue > 100 || currentValue !== correctPercentage) {
          bar.style.setProperty('--progress-bar-width', correctPercentage + '%');
        }
      }
    };
    const fixAllProgressBars = () => {
      document.querySelectorAll('progress-bar, [data-inventory-quantity]').forEach(fixProgressBarWidth);
    };
    fixAllProgressBars();
    const t1 = setTimeout(fixAllProgressBars, 50);
    const t2 = setTimeout(fixAllProgressBars, 200);
    const t3 = setTimeout(fixAllProgressBars, 500);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const bar = mutation.target;
          if (bar.tagName.toLowerCase() === 'progress-bar' || bar.hasAttribute('data-inventory-quantity')) {
            fixProgressBarWidth(bar);
          }
        }
      });
    });
    document.querySelectorAll('progress-bar, [data-inventory-quantity]').forEach(bar => {
      observer.observe(bar, { attributes: true, attributeFilter: ['style'] });
    });
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); observer.disconnect(); };
  }, []);

  const getCurrentPriceTier = (variant, qty) => {
    if (!variant.bulkPricing || variant.bulkPricing.length === 0) {
      return {
        unitPriceDisplay: variant.unitPriceDisplay || variant.per100g,
        excVAT: variant.excVAT,
        incVAT: variant.incVAT
      };
    }
    for (let i = variant.bulkPricing.length - 1; i >= 0; i--) {
      const tier = variant.bulkPricing[i];
      if (qty >= tier.min && qty <= tier.max) {
        return {
          unitPriceDisplay: tier.unitPriceDisplay || tier.per100g,
          excVAT: tier.excVAT,
          incVAT: tier.incVAT,
          range: tier.range
        };
      }
    }
    return {
      unitPriceDisplay: variant.unitPriceDisplay || variant.per100g,
      excVAT: variant.excVAT,
      incVAT: variant.incVAT
    };
  };

  useEffect(() => {
    if (!selectedVariant) return;
    const currentVariant = productData.variants.find(v => v.id === selectedVariant);
    if (!currentVariant) return;

    document.querySelectorAll('[name="id"]').forEach(input => {
      if (input.value != selectedVariant) {
        input.value = selectedVariant;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (currentVariant.inventory_quantity !== undefined) {
      document.querySelectorAll('progress-bar, [data-inventory-quantity]').forEach(bar => {
        const inventoryQty = currentVariant.inventory_quantity || 0;
        bar.setAttribute('data-quantity', inventoryQty);
        bar.setAttribute('data-inventory-quantity', inventoryQty);
        const max = parseInt(bar.getAttribute('data-max') || bar.getAttribute('max') || 100);
        bar.style.setProperty('--progress-bar-width', Math.min((inventoryQty / max) * 100, 100) + '%');
      });
      document.querySelectorAll('.product__inventory-item strong, .product__inventory strong').forEach(strong => {
        strong.textContent = currentVariant.inventory_quantity;
      });
    }
  }, [selectedVariant, quantity, productData]);

  const handleVariantSelect = (variantId) => { setSelectedVariant(variantId); };

  const handleAddToCart = async (e) => {
    if (e) e.preventDefault();
    if (!selectedVariant || isAddingToCart) return;
    const currentVariant = productData.variants.find(v => v.id === selectedVariant);
    if (!currentVariant || !currentVariant.available) {
      setMessage({ text: window.variantVisualizerTranslations?.unavailable || 'Variante non disponibile', type: 'error' });
      return;
    }
    const productForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) { variantInput.value = selectedVariant; variantInput.disabled = false; }
      const quantityInput = productForm.querySelector('input[name="quantity"]');
      if (quantityInput) { quantityInput.value = quantity; }

      const currentTier = getCurrentPriceTier(currentVariant, quantity);
      if (currentVariant.bulkPricing && currentVariant.bulkPricing.length > 0) {
        const basePrice = parseFloat(currentVariant.incVAT.replace('€', '').replace(',', '.'));
        const tierPrice = parseFloat(currentTier.incVAT.replace('€', '').replace(',', '.'));
        const discountPercent = Math.round(((basePrice - tierPrice) / basePrice) * 100);
        if (discountPercent > 0) {
          productForm.querySelectorAll('input[name^="properties[_bulk"]').forEach(el => el.remove());
          const props = {
            'properties[_bulk_pricing_enabled]': 'true',
            'properties[_original_price]': currentVariant.incVAT,
            'properties[_discounted_price]': currentTier.incVAT,
            'properties[_discount_percent]': discountPercent + '%',
            'properties[_tier]': currentTier.range || 'Standard',
            'properties[_quantity]': quantity.toString()
          };
          Object.entries(props).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden'; input.name = name; input.value = value;
            productForm.appendChild(input);
          });
        }
      }
      const submitButton = productForm.querySelector('button[type="submit"][name="add"]');
      if (submitButton && !submitButton.disabled) { submitButton.click(); }
      else if (productForm.requestSubmit) { productForm.requestSubmit(); }
      else { productForm.submit(); }
      return;
    }
  };

  if (!productData || !productData.variants || productData.variants.length === 0) { return null; }

  // Grid columns definition
  const gridCols = '2fr 1.2fr 1fr 1fr';

  return React.createElement('div', null,
    React.createElement('style', {
      dangerouslySetInnerHTML: {
        __html: `
          .price:not(.variant-visualizer-price),
          product-form .product-form__quantity,
          product-form quantity-input,
          product-form button[type="submit"][name="add"],
          product-form .shopify-payment-button {
            display: none !important;
          }

          .vv-card {
            background: #fff;
            border-radius: 16px;
            padding: 16px;
            box-shadow: 0 2px 16px rgba(44,95,82,0.08);
            font-family: var(--font-body-family, system-ui, sans-serif);
            width: 100%;
            max-width: 100%;
            margin: 0 0 20px 0;
          }

          .vv-header {
            display: grid;
            grid-template-columns: ${gridCols};
            padding: 0 8px 6px 8px;
            border-bottom: 1px solid #E8EFED;
            margin-bottom: 2px;
          }

          .vv-header div {
            font-size: 9px;
            font-weight: 700;
            color: #6B7B75;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            text-align: right;
          }

          .vv-header div:first-child {
            text-align: left;
          }

          .variant-visualizer-row {
            display: grid;
            grid-template-columns: ${gridCols};
            padding: 10px 8px;
            border-bottom: 1px solid #E8EFED;
            cursor: pointer;
            transition: background 0.15s ease;
            background: transparent;
            align-items: center;
          }

          .variant-visualizer-row:last-of-type {
            border-bottom: none;
          }

          .variant-visualizer-row:hover {
            background: #FAFBFA;
          }

          .variant-visualizer-row.selected {
            background: #F5F9F7;
            border-radius: 6px;
          }

          .variant-visualizer-row.unavailable {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .variant-visualizer-pack-size {
            font-size: 13.5px;
            font-weight: 600;
            color: #1C2B26;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .variant-visualizer-radio {
            width: 16px;
            height: 16px;
            flex-shrink: 0;
          }

          .variant-visualizer-per100g {
            font-size: 10px;
            font-style: italic;
            font-weight: 400;
            color: #6B7B75;
            text-align: right;
          }

          .variant-visualizer-per100g::before {
            content: '';
            margin-right: 2px;
          }

          .variant-visualizer-per100g::after {
            content: '';
            margin-left: 2px;
          }

          .vv-price-exc {
            font-size: 11px;
            font-style: italic;
            font-weight: 500;
            color: #000000; 
            text-align: right;
          }

          .vv-price-inc {
            font-size: 15px;
            font-weight: 700;
            color: #2E7D4F;
            text-align: right;
          }

          .variant-visualizer-bulk {
            grid-column: 1 / -1;
            padding: 12px 8px;
            background: linear-gradient(to bottom, #F8FAF9 0%, #FAFBFA 100%);
            border-top: 1px solid #E8EFED;
          }

          .variant-visualizer-bulk-row {
            display: grid;
            grid-template-columns: ${gridCols};
            padding: 6px 0;
            font-size: 13px;
            color: #1C2B26;
            align-items: center;
          }

          .variant-visualizer-bulk-row.active-tier {
            background: #EAF4F0;
            border-radius: 8px;
            font-weight: 600;
          }

          .vv-price-original {
            font-size: 12px;
            color: #999;
            text-decoration: line-through;
            font-weight: 400;
            margin-right: 4px;
          }

          .vv-reseller-badge {
            display: inline-block;
            background: linear-gradient(to bottom, #2E7D4F 0%, #2A6F46 100%);
            color: white;
            font-size: 9.5px;
            font-weight: 700;
            padding: 3px 8px;
            border-radius: 4px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 10px;
          }

          .variant-visualizer-quantity {
            display: flex;
            gap: 14px;
            align-items: center;
            padding: 12px 0 4px;
            border-top: 1px solid #E8EFED;
            margin-top: 6px;
          }

          .variant-visualizer-quantity-label {
            font-size: 10.5px;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #6B7B75;
          }

          .variant-visualizer-quantity-group {
            display: flex;
            border: 1.5px solid #E8EFED;
            border-radius: 8px;
            overflow: hidden;
            background: #fff;
          }

          .variant-visualizer-quantity-group:hover {
            border-color: #C8DDD8;
          }

          .variant-visualizer-quantity-btn {
            width: 36px;
            height: 36px;
            border: none;
            background: #fff;
            font-size: 16px;
            color: #1C2B26;
            cursor: pointer;
          }

          .variant-visualizer-quantity-btn:hover:not(:disabled) {
            background: #F5F9F7;
            color: #2E7D4F;
          }

          .variant-visualizer-quantity-btn:disabled {
            opacity: 0.4;
            cursor: not-allowed;
          }

          .variant-visualizer-quantity-input {
            width: 50px;
            border: none;
            border-left: 1px solid #E8EFED;
            border-right: 1px solid #E8EFED;
            text-align: center;
            font-size: 14px;
            font-weight: 600;
            color: #1C2B26;
            outline: none;
          }

          .variant-visualizer-quantity-input::-webkit-outer-spin-button,
          .variant-visualizer-quantity-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          .variant-visualizer-add-btn {
            flex: 1;
            height: 40px;
            background: linear-gradient(to bottom, #2E7D4F 0%, #2A6F46 100%);
            color: #fff;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(46,125,79,0.2);
          }

          .variant-visualizer-add-btn:hover:not(:disabled) {
            background: linear-gradient(to bottom, #35915C 0%, #2E7D4F 100%);
            box-shadow: 0 4px 12px rgba(46,125,79,0.3);
            transform: translateY(-1px);
          }

          .variant-visualizer-add-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .variant-visualizer-message {
            margin-top: 12px;
            padding: 10px 16px;
            border-radius: 8px;
            font-size: 13px;
            text-align: center;
          }

          .variant-visualizer-message.success {
            background: #EAF4F0;
            color: #2E7D4F;
            border: 1px solid #C8DDD8;
          }

          .variant-visualizer-message.error {
            background: #FEF0F0;
            color: #C0392B;
            border: 1px solid #F5C6CB;
          }

          @media (max-width: 768px) {
            .vv-card { border-radius: 12px; padding: 14px; }
            .variant-visualizer-quantity {
              flex-direction: row;
              gap: 10px;
              flex-wrap: nowrap;
            }
            .variant-visualizer-quantity-label { display: none; }
          }
        `
      }
    }),

    // Inventory
    React.createElement('div', {
      ref: (el) => {
        if (el && !el.dataset.inventoryMoved) {
          const beYoursInventory = document.querySelector('.product__inventory, ul[class*="inventory"]');
          if (beYoursInventory) { el.appendChild(beYoursInventory); el.dataset.inventoryMoved = 'true'; }
        }
      },
      style: { marginBottom: '16px' }
    }),

    // Card
    React.createElement('div', { className: 'vv-card' },

      // Reseller badge
      reseller.isReseller && React.createElement('div', { className: 'vv-reseller-badge' },
        reseller.label || 'Prezzo Rivenditore', ' -' + Math.round(reseller.discount * 100) + '%'
      ),

      // Header
      React.createElement('div', { className: 'vv-header' },
        React.createElement('div', null, ''),
        React.createElement('div', null, '           '),
        React.createElement('div', null, (window.variantVisualizerTranslations || {}).pricePerUnit || 'Prezzo/u'),
        React.createElement('div', null, (window.variantVisualizerTranslations || {}).vatExcl || 'IVA Escl.'),
        React.createElement('div', null, (window.variantVisualizerTranslations || {}).vatIncl || 'IVA Incl.')
      ),

      // Variants
      productData.variants.map(variant => {
          const isSelected = selectedVariant === variant.id;
          const tier = isSelected ? getCurrentPriceTier(variant, quantity) : null;

          const unitPriceInc = isSelected
            ? (tier.unitPriceDisplay || '-')
            : (variant.unitPriceDisplay || variant.per100g || '-');

          const unitPriceExc = calcUnitExcVAT(unitPriceInc);

          const incVAT = isSelected ? (tier.incVAT || '-') : (variant.incVAT || '-');
          const excVAT = calcExcVAT(incVAT);

          // Apply reseller discount
          const finalUnitExc = reseller.isReseller ? applyResellerDiscount(unitPriceExc) : unitPriceExc;
          const finalExcVAT = reseller.isReseller ? applyResellerDiscount(excVAT) : excVAT;
          const finalIncVAT = reseller.isReseller ? applyResellerDiscount(incVAT) : incVAT;

          // Rebuild unit display with suffix (e.g. "/kg")
          let finalUnitDisplay = finalUnitExc;
          if (reseller.isReseller && unitPriceExc !== '-') {
            const suffixMatch = unitPriceExc.match(/(\/.*)/);
            if (suffixMatch && finalUnitExc !== '-') {
              const numOnly = finalUnitExc.replace(suffixMatch[1], '');
              finalUnitDisplay = numOnly + suffixMatch[1];
            }
          }

          return React.createElement(React.Fragment, { key: variant.id },
            React.createElement('div', {
              className: `variant-visualizer-row ${isSelected ? 'selected' : ''} ${!variant.available ? 'unavailable' : ''}`,
              onClick: () => variant.available && handleVariantSelect(variant.id)
            },
              // Format + checkmark radio
              React.createElement('div', { className: 'variant-visualizer-pack-size' },
                React.createElement('div', {
                  className: 'variant-visualizer-radio',
                  style: {
                    border: isSelected ? '2px solid #2E7D4F' : '2px solid #B0BEB9',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isSelected ? '#2E7D4F' : 'white',
                    transition: 'all 0.2s ease'
                  }
                },
                  isSelected && React.createElement('span', {
                    style: { color: 'white', fontSize: '10px', fontWeight: '900' }
                  }, '\u2713')
                ),
                variant.title || variant.option1 || 'Variante'
              ),

              // Prezzo/u (s.IVA, tra parentesi)
              React.createElement('div', { className: 'variant-visualizer-per100g' },
                finalUnitDisplay || '-'
              ),

              // s. IVA
              reseller.isReseller && reseller.discount
                ? React.createElement('div', { className: 'vv-price-exc' },
                    React.createElement('span', { className: 'vv-price-original' }, excVAT),
                    ' ', finalExcVAT
                  )
                : React.createElement('div', { className: 'vv-price-exc' }, excVAT),

              // c. IVA
              reseller.isReseller && reseller.discount
                ? React.createElement('div', { className: 'vv-price-inc' },
                    React.createElement('span', { className: 'vv-price-original' }, incVAT),
                    ' ', finalIncVAT
                  )
                : React.createElement('div', { className: 'vv-price-inc' }, incVAT)
            ),

            // Bulk pricing
            variant.bulkPricing && (variant.showBulkPricingAlways || isSelected) &&
            React.createElement('div', { className: 'variant-visualizer-bulk' },
              variant.bulkPricing.map((bulk, idx) => {
                const isActiveTier = isSelected && quantity >= bulk.min && quantity <= bulk.max;

                const bUnitInc = bulk.unitPriceDisplay || bulk.per100g || '-';
                const bUnitExc = calcUnitExcVAT(bUnitInc);
                const bExcVAT = calcExcVAT(bulk.incVAT);
                const bIncVAT = bulk.incVAT || '-';

                const fbUnitExc = reseller.isReseller ? applyResellerDiscount(bUnitExc) : bUnitExc;
                const fbExcVAT = reseller.isReseller ? applyResellerDiscount(bExcVAT) : bExcVAT;
                const fbIncVAT = reseller.isReseller ? applyResellerDiscount(bIncVAT) : bIncVAT;

                let fbUnitDisplay = fbUnitExc;
                if (reseller.isReseller && bUnitExc !== '-') {
                  const sm = bUnitExc.match(/(\/.*)/);
                  if (sm && fbUnitExc !== '-') {
                    fbUnitDisplay = fbUnitExc.replace(sm[1], '') + sm[1];
                  }
                }

                return React.createElement('div', {
                  key: idx,
                  className: `variant-visualizer-bulk-row ${isActiveTier ? 'active-tier' : ''}`
                },
                  React.createElement('div', null, bulk.range),

                  React.createElement('div', { className: 'variant-visualizer-per100g' },
                    fbUnitDisplay || '-'
                  ),

                  reseller.isReseller && reseller.discount
                    ? React.createElement('div', { className: 'vv-price-exc' },
                        React.createElement('span', { className: 'vv-price-original' }, bExcVAT),
                        ' ', fbExcVAT
                      )
                    : React.createElement('div', { className: 'vv-price-exc' }, bExcVAT),

                  reseller.isReseller && reseller.discount
                    ? React.createElement('div', { className: 'vv-price-inc' },
                        React.createElement('span', { className: 'vv-price-original' }, bIncVAT),
                        ' ', fbIncVAT
                      )
                    : React.createElement('div', { className: 'vv-price-inc' }, bIncVAT)
                );
              })
            )
          );
        }),

      // Controls
      React.createElement('div', { className: 'variant-visualizer-quantity' },
        React.createElement('div', { className: 'variant-visualizer-quantity-label' },
          window.variantVisualizerTranslations?.quantity || 'Quantità'),
        React.createElement('div', { className: 'variant-visualizer-quantity-group' },
          React.createElement('button', {
            className: 'variant-visualizer-quantity-btn',
            onClick: () => setQuantity(Math.max(1, quantity - 1)),
            disabled: quantity <= 1
          }, '\u2212'),
          React.createElement('input', {
            type: 'text',
            inputMode: 'numeric',
            pattern: '[0-9]*',
            className: 'variant-visualizer-quantity-input',
            value: quantity,
            onChange: (e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              setQuantity(Math.max(1, parseInt(value) || 1));
            },
            onBlur: (e) => {
              if (!e.target.value || parseInt(e.target.value) < 1) setQuantity(1);
            }
          }),
          React.createElement('button', {
            className: 'variant-visualizer-quantity-btn',
            onClick: () => setQuantity(quantity + 1)
          }, '+')
        ),
        React.createElement('button', {
          className: 'variant-visualizer-add-btn',
          onClick: handleAddToCart,
          disabled: isAddingToCart || !selectedVariant || !productData.variants.find(v => v.id === selectedVariant)?.available
        }, isAddingToCart
          ? (window.variantVisualizerTranslations?.adding || 'Aggiungendo...')
          : (window.variantVisualizerTranslations?.addToCart || 'Aggiungi al Carrello'))
      ),

      // Message
      message.text && React.createElement('div', {
        className: `variant-visualizer-message ${message.type}`
      }, message.text)
    )
  );
};

window.VariantVisualizer = VariantVisualizer;
